import React, { useState, useEffect } from "react"
import { Modal, Button } from "react-bootstrap"
import { FormattedMessage, useIntl } from "react-intl"
import PropTypes from "prop-types"
import moment from "moment"

import { API_STATUS, Required } from "../../../../shared_components/widgets/utils"
import PageLoadingPanel from "../../../../shared_components/widgets/PageLoadingPanel"
import PageErrorPanel from "../../../../shared_components/widgets/PageErrorPanel"
import { addNewObject } from "../../../../Loaders"
import { logToServer } from "../../../../ErrorBoundary"
import { withInventoryConfig } from "../../../../config/InventoryConfigProvider"
import ReactSelectionElement from "../../../../shared_components/widgets/ReactSelectionElement"

function ModalCreateProcurementItem({
  onHide,
  show,
  onSuccess,
  // From InventoryConfigProvider
  getInventoryTypes,
  inventoryTypes = [],
  statusInventoryTypes,
  inventoryItems = [],
  // ---
}) {
  const intl = useIntl()
  const [status_submit, setStatusSubmit] = useState(null)
  const [formErrors, setFormErrors] = useState({})
  const [formValues, setFormValues] = useState({
    name: "",
    description: "",
    inventory_type_id: "",
    lot_no: "",
    uom: "",
    quantity: "",
    retest_date: "",
    expected_receive_date: "",
  })

  const [existingSkuOpts, setExistingSkuOpts] = useState([])
  const [skuSelected, setSkuSelected] = useState({})
  useEffect(() => {
    const distinctSku = [...new Set(inventoryItems.map((it) => it?.sku?.name))]
    const skuOptions = []
    distinctSku.forEach((name) => {
      const match = inventoryItems.find((it) => it?.sku?.name === name)
      const sku = match?.sku || {}
      const inv = sku.inventory_type?.name
      skuOptions.push({
        name: sku.name,
        description: sku.description,
        label: `${inv}: ${sku.name} (${sku.description})`,
        inv_type_name: inv,
        inv_type_id: sku.inventory_type?.id,
      })
    })
    setExistingSkuOpts(skuOptions.sort((a, b) => a["inv_type_name"].localeCompare(b["inv_type_name"])))
  }, [inventoryItems])

  const onChangeValue = (event) => {
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value,
    })
  }

  useEffect(() => {
    const match = existingSkuOpts.find(
      (sku) => sku.name.toLowerCase() === (formValues.name || "").toLowerCase(),
    )
    if (match) {
      setSkuSelected(match)
      setFormValues({
        ...formValues,
        name: match.name,
        description: match.description,
        inventory_type_id: match.inv_type_id,
      })
    } else {
      skuSelected?.inv_type_id && setSkuSelected({})
    }
  }, [formValues.name])

  const onSubmit_create = (event) => {
    event.preventDefault()
    setStatusSubmit(API_STATUS.LOADING)
    const dateMetrics = ["expected_receive_date", "retest_date"]
    const finalData = Object.assign({}, formValues)
    dateMetrics.forEach((key) => (finalData[key] = moment(finalData[key]).format("YYYY-MM-DDTHH:mm:ssZZ")))

    addNewObject("/laboratory/inventory/procurement-item/", finalData, true)
      .then((data) => {
        setStatusSubmit(API_STATUS.SUCCESS)
        onSuccess(data)
      })
      .catch((error) => {
        logToServer(`Couldn't add Procurement Item: ${error.statusText}`)
        console.error(error.statusText)
        error.text().then((err) => {
          logToServer(err)
          console.error(err)
          try {
            err = JSON.parse(err)
            setFormErrors(err)
          } catch (e) {}
        })
        setStatusSubmit(null)
        alert(intl.formatMessage({ id: "inventory.error-unable-to-create-item" }))
      })
  }

  const dropdownPlaceholder = intl.formatMessage({
    id: "general.dropdown.please.select",
    defaultMessage: "-- Please Select --",
  })

  const fontStyle = { fontSize: "85%" }

  return (
    <Modal size={"lg"} show={show} onHide={onHide} backdrop={"static"} keyboard={true}>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={"fa fa-plus"} />
          &nbsp;&nbsp;
          <FormattedMessage id={"inventory.add-new-item"} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {statusInventoryTypes === API_STATUS.LOADING && <PageLoadingPanel />}

        {statusInventoryTypes === API_STATUS.ERROR && <PageErrorPanel attemptReload={getInventoryTypes} />}

        {statusInventoryTypes === API_STATUS.SUCCESS && (
          <form
            name={"form_create_procurement_item"}
            className={"form-horizontal"}
            onSubmit={onSubmit_create}
            style={{ width: "700px" }}
          >
            <div
              className={"row"}
              style={{
                margin: "15px 0",
                padding: "10px 0",
                backgroundColor: "var(--lanzalake-grey-light-2)",
                ...fontStyle,
              }}
            >
              <div className={"col-7"} style={{ borderRight: "1px solid var(--lanzalake-grey)" }}>
                <b>
                  <label style={{ margin: "0" }}>
                    <FormattedMessage id={"general.create"} defaultMessage={"Create"} />:
                  </label>
                </b>
                <hr />
                {[
                  ["general.name", "Name", "name", "text"],
                  ["general.description", "Description", "description", "text"],
                  ["general.type", "Type", "inventory_type_id", "inv_select"],
                ].map((entry, entryIdx) => (
                  <div
                    className={"row"}
                    key={`formEntry${entryIdx}`}
                    style={{ margin: "0", marginTop: "5px" }}
                  >
                    <div className={"col-4"}>
                      <label>
                        <FormattedMessage id={entry[0]} defaultMessage={entry[1]} />: &nbsp;
                        <Required />
                      </label>
                    </div>
                    <div className={"col-8"}>
                      <div style={{ width: "100%" }}>
                        {["text", "number", "date"].includes(entry[3]) ? (
                          <input
                            type={entry[3]}
                            className={"form-control"}
                            name={entry[2]}
                            value={formValues[entry[2]]}
                            onChange={onChangeValue}
                            disabled={!!(skuSelected?.inv_type_id && entry[2] !== "name")}
                            required
                          />
                        ) : (
                          entry[3] === "inv_select" && (
                            <ReactSelectionElement
                              name={entry[2]}
                              list={inventoryTypes}
                              placeholder={dropdownPlaceholder}
                              onValueChange={(name, selected) =>
                                setFormValues({
                                  ...formValues,
                                  [name]: selected?.id || "",
                                })
                              }
                              value={
                                formValues[entry[2]]
                                  ? inventoryTypes.find((it) => it.id === formValues[entry[2]])
                                  : ""
                              }
                              getOptionValue={(obj) => obj.id}
                              getOptionLabel={(obj) => obj.name}
                              isMulti={false}
                              disabled={skuSelected?.inv_type_id}
                            />
                          )
                        )}
                        {formErrors[entry[2]] && (
                          <span className={"form-text error-message"}>{formErrors[entry[2]]}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className={"col-5"}>
                <b>
                  <label style={{ margin: "0" }}>
                    <FormattedMessage id={"general.choose"} defaultMessage={"Choose"} />
                    &nbsp;
                    <span style={fontStyle}>{`(${intl.formatMessage({
                      id: "general.optional",
                      defaultMessage: "Optional",
                    })})`}</span>
                    :
                  </label>
                </b>
                <hr />
                <ReactSelectionElement
                  name={"existingSku"}
                  list={existingSkuOpts}
                  placeholder={dropdownPlaceholder}
                  onValueChange={(_, selected) => {
                    setSkuSelected(selected || {})
                    setFormValues({
                      ...formValues,
                      name: selected?.name || "",
                      description: selected?.description || "",
                      inventory_type_id: selected?.inv_type_id || "",
                    })
                  }}
                  value={skuSelected["inv_type_id"] ? skuSelected : ""}
                  getOptionValue={(obj) => obj.inv_type_id}
                  getOptionLabel={(obj) => obj.label}
                  isMulti={false}
                />
              </div>
            </div>
            {[
              ["stock.lot", "Lot #", "lot_no", "text"],
              ["stock.uom", "UoM", "uom", "text"],
              ["stock.quantity", "Quantity", "quantity", "number"],
              ["stock.retest_date", "Retest Date", "retest_date", "date"],
              ["stock.expected_receive_date", "Expected Receive Date", "expected_receive_date", "date"],
            ].map((entry, entryIdx) => (
              <div
                className={"row"}
                key={`formEntry${entryIdx}`}
                style={{ margin: "0", marginTop: "5px", ...fontStyle }}
              >
                <div className={"col-3"}>
                  <label>
                    <FormattedMessage id={entry[0]} defaultMessage={entry[1]} />: &nbsp;
                    <Required />
                  </label>
                </div>
                <div className={"col-9"}>
                  <div style={{ width: "100%" }}>
                    {["text", "number", "date"].includes(entry[3]) && (
                      <input
                        type={entry[3]}
                        className={"form-control"}
                        name={entry[2]}
                        value={formValues[entry[2]]}
                        onChange={onChangeValue}
                        required
                      />
                    )}
                    {formErrors[entry[2]] && (
                      <span className={"form-text error-message"}>{formErrors[entry[2]]}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div align={"center"}>
              {status_submit === API_STATUS.LOADING && (
                <div>
                  <div className="spinner-border text-primary text-center" role="status" />
                  &nbsp;
                  <FormattedMessage id={"general.saving"} />
                  ...
                </div>
              )}

              {status_submit === null && (
                <Button variant={"primary"} type={"submit"}>
                  <FormattedMessage id={"general.save"} />
                </Button>
              )}
            </div>
          </form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant={"outline-primary"} onClick={onHide} style={{ display: "flex", float: "right" }}>
          <FormattedMessage id={"general.closeButton"} />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default withInventoryConfig(ModalCreateProcurementItem)

ModalCreateProcurementItem.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
}
